import { useState, useEffect, useCallback } from 'react';
import config from '../config';
import { getStoredToken } from '../services/tokenRefreshService';

// ============================================================================
// Types
// ============================================================================

export interface ChannelSoundPreferences {
  alert: 'notification-1' | 'notification-2' | 'none';
  muted: boolean;
  volume: number; // 0-100
}

export interface UserPreferences {
  theme: 'light' | 'dark';
  /** Legacy single order-alert setting. Superseded by deeliverSound/uberSound; kept for old saves. */
  sound: {
    orderAlert: 'notification-1' | 'notification-2' | 'none';
    muted: boolean;
    volume: number; // 0-100
  };
  /** New Deeliver app order alert — set from the Orders page. */
  deeliverSound: ChannelSoundPreferences;
  /** New Uber Eats order alert — set from the Uber Orders page. */
  uberSound: ChannelSoundPreferences;
  ordersHud: {
    viewMode: 'list' | 'kanban' | 'cards';
  };
}

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

// ============================================================================
// Defaults
// ============================================================================

const DEFAULTS: UserPreferences = {
  theme: 'light',
  sound: { orderAlert: 'notification-1', muted: false, volume: 70 },
  // Separate keys (not one nested object) because preference merges are one level deep.
  deeliverSound: { alert: 'notification-2', muted: false, volume: 70 },
  uberSound: { alert: 'notification-1', muted: false, volume: 70 },
  ordersHud: { viewMode: 'list' },
};

// ============================================================================
// Module-level singleton state
// ============================================================================

let cached: UserPreferences | null = null;
let inflight: Promise<void> | null = null;
let loaded = false;

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => fn());
}

// ============================================================================
// Shallow-deep merge (one level of nesting)
// ============================================================================

function merge<T extends object>(base: T, patch: DeepPartial<T>): T {
  const result = { ...base } as T;
  for (const key of Object.keys(patch) as Array<keyof T>) {
    const pv = patch[key];
    const bv = result[key];
    if (
      pv !== null &&
      pv !== undefined &&
      typeof pv === 'object' &&
      !Array.isArray(pv) &&
      typeof bv === 'object' &&
      bv !== null
    ) {
      result[key] = { ...bv, ...(pv as object) } as T[typeof key];
    } else if (pv !== undefined) {
      result[key] = pv as T[typeof key];
    }
  }
  return result;
}

// ============================================================================
// localStorage → backend one-time migration
// ============================================================================

function consumeLocalStorageMigration(): DeepPartial<UserPreferences> | null {
  const patch: DeepPartial<UserPreferences> = {};
  let hasPatch = false;

  const lsTheme = localStorage.getItem('themeMode');
  if (lsTheme === 'dark' || lsTheme === 'light') {
    patch.theme = lsTheme;
    hasPatch = true;
    localStorage.removeItem('themeMode');
  }

  const lsSound = localStorage.getItem('order-sound-settings');
  if (lsSound) {
    try {
      const parsed = JSON.parse(lsSound);
      patch.sound = {
        orderAlert: parsed.sound ?? 'notification-1',
        muted: parsed.muted ?? false,
        volume: parsed.volume ?? 70,
      };
      hasPatch = true;
      localStorage.removeItem('order-sound-settings');
    } catch {
      localStorage.removeItem('order-sound-settings');
    }
  }

  return hasPatch ? patch : null;
}

// ============================================================================
// Debounced backend save
// ============================================================================

let saveTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Patches made before the backend answered, newest last.
 *
 * Without this an early update had to invent a base to merge onto, and the only
 * base available was DEFAULTS — which is how a saved `theme: 'dark'` got
 * overwritten with light by an unrelated preference change.
 */
let pendingPatches: DeepPartial<UserPreferences>[] = [];

function queuePatch(patch: DeepPartial<UserPreferences>) {
  pendingPatches.push(patch);
}

/** Fold any queued patches onto the freshly-loaded preferences and save once. */
function flushPendingPatches() {
  if (pendingPatches.length === 0 || !cached) return;
  for (const patch of pendingPatches) cached = merge(cached, patch);
  pendingPatches = [];
  scheduleSave(cached);
}

function scheduleSave(prefs: UserPreferences) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      const token = getStoredToken();
      if (!token) return;
      await fetch(`${config.API_URL}/admin/users/me/preferences`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(prefs),
      });
    } catch {
      // Ignore save errors — preferences are best-effort
    }
  }, 1000);
}

// ============================================================================
// Fetch from backend
// ============================================================================

function fetchPreferences(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const token = getStoredToken();
      if (!token) return;

      const res = await fetch(`${config.API_URL}/admin/users/me/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;

      const data = await res.json();
      // Server may omit keys it has never been told about — fill those from
      // DEFAULTS rather than leaving them undefined.
      cached = merge(DEFAULTS, (data.preferences ?? {}) as DeepPartial<UserPreferences>);
      loaded = true;

      // One-time localStorage migration
      const migrationPatch = consumeLocalStorageMigration();
      if (migrationPatch) {
        cached = merge(cached, migrationPatch);
        scheduleSave(cached);
      }

      // Anything the user changed while this request was in flight.
      flushPendingPatches();

      notify();
    } catch {
      // Leave `loaded` false so the next mount retries. Deliberately NOT adopting
      // DEFAULTS here: a transient failure must not become a write of light mode
      // over whatever the server actually has saved.
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

// ============================================================================
// Hook
// ============================================================================

export function useUserPreferences() {
  const [prefs, setPrefs] = useState<UserPreferences>(cached ?? DEFAULTS);

  useEffect(() => {
    const rerender = () => setPrefs(cached ?? DEFAULTS);
    listeners.add(rerender);

    if (!loaded) {
      fetchPreferences();
    }

    return () => {
      listeners.delete(rerender);
    };
  }, []);

  /**
   * Apply a patch locally and persist it.
   *
   * The merge base MUST be the loaded preferences, never DEFAULTS. This used to
   * do `merge(cached ?? DEFAULTS, patch)` and then PUT the whole object back —
   * so any unrelated update made before the backend responded (or after a cache
   * clear on token refresh / logout) rebuilt the object from DEFAULTS and wrote
   * `theme: 'light'` over the user's saved dark mode. That is the "admin dash
   * randomly goes light by itself" bug: muting a sound or switching the orders
   * HUD view was enough to do it, and because it saved, it stuck.
   *
   * While preferences are still loading we queue the patch instead of guessing,
   * and apply it once the real values arrive.
   */
  const updatePreferences = useCallback((patch: DeepPartial<UserPreferences>) => {
    if (!loaded || !cached) {
      queuePatch(patch);
      // Reflect it locally so the UI responds immediately; the queued patch is
      // what actually reaches the backend, once there's a real base to merge on.
      setPrefs((prev) => merge(prev, patch));
      // Make sure something is actually trying to load, or the patch sits queued.
      void fetchPreferences();
      return;
    }
    const next = merge(cached, patch);
    cached = next;
    setPrefs(next);
    notify();
    scheduleSave(next);
  }, []);

  return {
    preferences: prefs,
    updatePreferences,
    /** True once the backend response has been received */
    isLoaded: loaded,
  };
}

/** Reset module cache — used for logout */
export function clearUserPreferencesCache() {
  cached = null;
  loaded = false;
  inflight = null;
  // Drop queued patches too — they belong to the session being torn down, and
  // replaying them onto the next user's preferences would be wrong.
  pendingPatches = [];
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
}
